import { Sprite, Texture, Graphics, Assets, Text, type Container } from 'pixi.js';
import { CANVAS_FONT } from '../../fonts';
import { el, toast } from '../../dom';
import { fieldApi, shopApi, type Building } from '../../game-api';
import { tutorialStage } from '../../tutorial/state';
import { TILE } from '../../map/world';

// A finger tap wobbles. 6px (the camera's PAN threshold) rejects real taps on a
// touchscreen; 18px still separates a tap from any deliberate pan.
const TAP_SLOP = 18;
import { type MapTextures, type OverlayKey } from '../../map/textures';
import { FURNACE_BURN, LIGHT_BUILDINGS, LIGHT_FPS, furnaceFrames, lightFrames } from '../../map/lights';
import { PX, cropById, cropStage, closePlanter, openOverlay, dirToTile, guard, liveRemaining, plotAt, BUILDINGS, HOE_CURSOR, type FarmCtx, refreshBag, setFarm,} from './context';
import { inToolReach, hoeAction, requestPlot, toolTap } from './input';
import { buildCollision, clearDecorUnderPlots } from './scene';
import { fenceSprite, isFenceKind } from '../../map/fence-connect';
import { isGroundKind } from '../../map/decor-kinds';
import { isRoadKind, roadSprite } from '../../map/road-connect';
import { paintHotbar, selectHotbarTool } from './hud';
import { affordEnergy } from './resources';
import { modalWrap } from '../../ui/panel';
import { playSfx } from '../../audio/audio';
import { canRushCrop, fmtCountdown, isUnderConstruction, openCropRush, requestFinishRefresh, setConstructionRepainter } from './construction';
import { openBuildingMenu } from './building-menu';
import { buildSeedList, waterNotice } from './planting-panel';
import { growthFraction } from './planting';

// The 1 Hz repaint is what makes a countdown tick and what swaps scaffolding for
// the finished art once a fresh snapshot lands; the rush panel needs it after a
// purchase, but construction.ts cannot import this module back (cycle).
setConstructionRepainter(paintPlots, buildCollision);

/**
 * Mark a sprite as pure decoration — invisible to the hit test.
 *
 * THE bug behind "you cannot tap a building under construction". `app.stage` is
 * `eventMode: 'static'` (map/camera.ts), and Pixi 8 passes an interactive mode
 * DOWN to every descendant (EventBoundary.hitTestRecursive: the child's own
 * eventMode is only consulted when the inherited one is not interactive). At the
 * leaf, a sprite that CONTAINS the point but is not itself interactive returns an
 * empty path — which is non-null, so the parent stops scanning the remaining
 * (lower) siblings and the whole tap resolves to the stage → click-to-walk.
 *
 * A default `'passive'` sprite therefore does not merely fail to receive taps: it
 * EATS them for everything drawn beneath it, over its full bounds rectangle
 * (containsPoint is a plain rect test — alpha and transparency are irrelevant).
 * The scaffold frame, the countdown label and the two progress-bar quads exist
 * only while a building is under construction and cover its centre, its top and
 * the tile strip you naturally aim at — which is exactly why a FINISHED building
 * opened its menu and a half-built one never did.
 *
 * `'none'` is the one mode `_interactivePrune` drops outright, so a sprite marked
 * here is skipped and the scan continues to the building sprite below it. Every
 * non-interactive sprite this module creates goes through this helper.
 */
function decorative<T extends Container>(s: T): T {
  s.eventMode = 'none';
  return s;
}

/**
 * What a pointer press on a placed building should do. Pure, so the routing is
 * testable without a Pixi hit test (jsdom has no layout engine, and this decision
 * is the thing that kept regressing).
 *
 *  'walk'   — finished ground (road/path/bridge deck) is FLOOR: let the tap fall
 *             through to click-to-walk, or you cannot cross your own bridge.
 *  'tool'   — fences come down by swinging the matching tool within reach, so
 *             their tap belongs to the tool path, not to a menu.
 *  'ignore' — the gesture travelled too far to be a tap (a camera pan).
 *  'place'  — build mode is armed: the click belongs to placement.
 *  'open'   — select the building: use / rush / move / demolish.
 *
 * Note the ordering: ground UNDER CONSTRUCTION does not get 'walk'. A bridge
 * takes minutes and stands as scaffolding you cannot walk on, so its tap has to
 * be able to reach the Finish-now panel.
 */
export type BuildingRoute = 'walk' | 'tool' | 'ignore' | 'place' | 'open';
export function buildingPointerRoute(a: {
  kind: string; underConstruction: boolean; buildMode: boolean; dragMagnitude: number;
}): BuildingRoute {
  if (isGroundKind(a.kind) && !a.underConstruction) return 'walk';
  if (isFenceKind(a.kind)) return 'tool';
  // `dragMagnitude` accumulates |dx|+|dy| over the whole press against the
  // camera's 6px PAN threshold, which an ordinary finger tap exceeds — so the
  // stricter flag classified real touches as pans. TAP_SLOP allows a wobble.
  if (a.dragMagnitude > TAP_SLOP) return 'ignore';
  if (a.buildMode) return 'place';
  return 'open';
}

/**
 * Carry out a routed building tap. `gx`/`gy` are Pixi GLOBAL (canvas CSS px)
 * coordinates — only 'place' needs them, to hand the click to the placement
 * validator, which answers (and explains) whether the tile is free.
 */
function runBuildingRoute(c: FarmCtx, b: Building, r: BuildingRoute, gx: number, gy: number): void {
  if (r === 'ignore' || r === 'walk' || r === 'tool') return;
  if (r === 'place') {
    const cam = c.cam;
    if (!cam) return;
    const sc = cam.getScale();
    toolTap(c, (gx - cam.world.x) / sc, (gy - cam.world.y) / sc);
    return;
  }
  // Outside build mode a tap SELECTS: one small panel offering use / rush / move /
  // demolish — and, while the building is still scaffolding, "Finish now (Shrooms)",
  // which is the panel that confirms before anything is spent.
  openBuildingMenu(c, b);
}

// ─── Press rescue (the 1 Hz repaint destroys sprites mid-gesture) ─────────────
// Set on pointerdown over a building, cleared by that building's own pointertap.
// If the tap never arrives because paintPlots destroyed the sprite in between, the
// window listener below finishes the gesture from the press we recorded.
let press: { c: FarmCtx; b: Building; sprite: Sprite } | null = null;
let rescueArmed = false;

function armPressRescue(): void {
  if (rescueArmed || typeof window === 'undefined') return;
  rescueArmed = true;
  // CAPTURE, so it runs before Pixi's own canvas pointerup handler and the panel
  // is already open by the time the (mis-targeted) tap is dispatched.
  window.addEventListener('pointerup', (ev: PointerEvent) => {
    const p = press;
    press = null;
    // Sprite still alive → its own pointertap will fire in a moment and handle it.
    if (!p || !p.sprite.destroyed || !p.c.alive) return;
    const r = buildingPointerRoute({
      kind: p.b.kind,
      underConstruction: p.b.isReady === false,
      buildMode: !!p.c.buildMode,
      dragMagnitude: p.c.cam?.dragMagnitude ?? 0,
    });
    if (r !== 'open' && r !== 'place') return;
    // Pixi global coords are CSS px relative to the canvas.
    const rect = p.c.app?.canvas?.getBoundingClientRect?.();
    if (!rect) return;
    runBuildingRoute(p.c, p.b, r, ev.clientX - rect.left, ev.clientY - rect.top);
    // This gesture is spent. The stage's pointertap (click-to-walk) is about to
    // fire on the layer the destroyed sprite left behind; `dragged` is exactly the
    // flag onTap bails on, and the camera clears it on the next pointerdown.
    if (p.c.cam) p.c.cam.dragged = true;
  }, true);
}

/** Test seam: forget a recorded press between cases. */
export function __resetBuildingPress(): void { press = null; }

/**
 * Countdown label above a building/crop. Pixi Text at 10px is unreadable against
 * grass without an outline, so every timer gets the same dark stroke the damage
 * numbers use.
 */
function timerLabel(text: string, x: number, y: number, z: number): Text {
  const t = new Text({ text, style: { fontFamily: CANVAS_FONT, fontSize: 10, fill: 0xffe9b8, fontWeight: 'bold', stroke: { color: 0x1c150c, width: 3 } } });
  t.anchor.set(0.5, 1);
  t.x = x; t.y = y; t.zIndex = z;
  return decorative(t);
}

// Building sprites are baked from the pack into /sprites/buildings. Pixi v8's
// Texture.from does NOT fetch an un-preloaded network image (it returns an empty
// texture), which is why placed buildings rendered invisibly. Load through
// Assets (cached after the first hit) and swap the texture in when it arrives.
const bldgCache = new Map<string, Texture>();
/** Structures baked at native pack size (multi-tile art), not as 16px tiles. */
const BIG_BUILDINGS = new Set(['hut', 'house', 'farmhouse', 'lumber_hut', 'mine_shaft', 'forage_hut', 'coop', 'barn']);

/**
 * Purely VISUAL weight bump ("building should be a bit bigger" — owner). It scales
 * the drawn sprite only: the collision grid and the tile a building occupies are
 * derived from b.tx/b.ty in scene.ts and from the server BUILDINGS config, and are
 * deliberately left alone — so a house that now LOOKS wider still blocks exactly the
 * same tiles. Do not "fix" that mismatch by widening the footprint to match the art.
 * The sprite anchor is (0.5, 1) pinned to the tile's bottom edge, so the extra size
 * grows upward/sideways and the base stays planted on its tile.
 */
export const BUILDING_ART_SCALE = 1.2;

/**
 * The bump the multi-tile structures get instead.
 *
 * The full 1.2 was applied to art that is ALREADY three to four tiles wide, so a
 * farmhouse overhung each neighbouring column by ~1.3 tiles and two structures on
 * neighbouring rows read as one smeared pile — the owner's "overlay of the sprites".
 * Shrinking them back to 1.0 would undo the "a bit bigger" ask, so the bump is kept
 * but scaled to the art: a small station (one tile of art) can afford +20% because
 * 20% of one tile is a few pixels of overhang, while +20% of a barn is a whole tile.
 * 1.08 keeps the visible weight gain and roughly halves the overhang.
 */
export const BIG_ART_SCALE = 1.08;

/**
 * Kinds that must keep their exact 1-tile size and therefore never get the bump:
 * fences AUTOTILE — a run is drawn edge-to-edge from 16px pieces, so anything other
 * than TILE/16 leaves gaps or overlaps at every joint. Lights (torch/lantern) are
 * the same 16px grid art and are sized to sit flush in a tile.
 */
function isExactTileKind(kind: string): boolean {
  // Ground decoration joins them: road, path and bridge-deck art is all baked as
  // a single 16px tile laid edge-to-edge, so the 1.2 bump would overlap the
  // neighbouring tile by 20% and break every joint in a paved run or a span.
  return isFenceKind(kind) || isGroundKind(kind) || kind in LIGHT_BUILDINGS;
}

/**
 * Per-kind art correction, for sprites the shared scale gets wrong in EITHER
 * direction.
 *
 * The structure scale is one number applied to art assumed to be 70-100px wide, so
 * anything far off that mis-reads:
 *  - The Mine Shaft (32x41 cave door) and Forager Hut (46x38 shack) landed barely
 *    over a tile and looked like scenery props rather than the workplace they cost
 *    24 stone to build — hence a boost.
 *  - The crafting table is the opposite problem. Its art is a 34x39 bench, which at
 *    the shared scale drew 2.6 tiles wide and 2.9 TALL — a workbench towering over
 *    the furnace beside it and taller than the hero. Hence a shrink, to about a tile
 *    and a half: wider than the anvil, shorter than the furnace, which is what a
 *    bench should read as.
 *
 * Nothing else changes: the collision footprint and the occupied tile come from
 * b.tx/b.ty, never from the drawn size (see BUILDING_ART_SCALE).
 */
const ART_SCALE_FIX: Record<string, number> = {
  mine_shaft: 1.8,
  forage_hut: 1.5,
  workbench: 0.58,
  // NO entry for the forge. It got a 1.4x nudge here on the theory that an anvil
  // is too small to read as the top-tier station; the owner's answer was that the
  // furnace and forge had become bigger sprites and to change that back. The anvil
  // draws at its own size like every other building.
};

/** Sprite scale for a placed building. Exported so the exclusions are testable. */
export function buildingScale(kind: string): number {
  // Torches/fences are 16px art → 1 tile at TILE/16. Full structures (the
  // house and the village buildings) are baked at their native pack size —
  // multi-tile cottages/barns — so they get their own, smaller scale.
  const big = BIG_BUILDINGS.has(kind);
  const base = big ? 1.4 : TILE / 16;
  return isExactTileKind(kind) ? base : base * (big ? BIG_ART_SCALE : BUILDING_ART_SCALE) * (ART_SCALE_FIX[kind] ?? 1);
}

function loadBuildingTex(kind: string, onReady: (t: Texture) => void): void {
  const url = `/sprites/buildings/${kind}.png`;
  const cached = bldgCache.get(kind);
  if (cached) { onReady(cached); return; }
  void Assets.load(url).then((t: Texture) => { bldgCache.set(kind, t); onReady(t); }).catch(() => { /* missing sprite */ });
}

/**
 * Depth key for a building sprite, y-sorted with the hero and the trees.
 *
 * The row alone (`ty*TILE + TILE`) gave two structures on the SAME row an identical
 * key, so Pixi's stable sort fell back to insertion order — i.e. the order the API
 * happened to list the buildings in — and which of two overlapping barns clipped
 * over the other could flip between snapshots. That flicker is what reads as a
 * glitch rather than as depth. The sub-pixel tx term is far too small to disturb
 * row ordering (a whole row is TILE apart) but makes the tie deterministic: on a
 * shared row the WEST building draws behind its east neighbour, always.
 */
export const buildingZ = (tx: number, ty: number): number => ty * TILE + TILE + tx * 1e-3;

/**
 * Which furnace sprite a tile draws, from the farm's smelt load. Three states,
 * one per state of the fire, which is what makes a working furnace readable from
 * across the farm:
 *   no load            → cold (`furnace`, the plain sprite)
 *   load still burning → null, so the caller runs the animated burn strip
 *   load finished      → `furnace_lit`: embers over bars nobody has collected,
 *                        the nudge to walk over and open it
 * Returns null for anything that is not a furnace, so the caller falls back to
 * the kind's own sprite. Exported for the state test — this is the payoff the
 * owner asked for and it must not silently regress to one frozen frame.
 */
export function furnaceSprite(kind: string, smelting?: { burning: boolean } | null): string | null {
  if (kind !== 'furnace') return null;
  if (!smelting) return 'furnace';
  return smelting.burning ? null : 'furnace_lit';
}

/** Longest remaining build time seen per tile — the denominator of its progress bar. */
const buildTotals = new Map<string, number>();
/**
 * Denominator of one tile's build progress bar, kept PER TILE so two builds running
 * at once can never borrow each other's total (a 60-minute farmhouse next to a
 * 2-minute workbench would otherwise draw one of the two bars at a wild fraction).
 * Exported for the keying test.
 */
export function buildBarTotal(tx: number, ty: number, fullMs: number, remainMs: number): number {
  const key = `${tx},${ty}`;
  // The kind's FULL duration wins; the session maximum is only the fallback for a
  // kind we have no duration for (anchoring on it alone lied after a reload).
  const total = Math.max(fullMs, remainMs, buildTotals.get(key) ?? 0);
  buildTotals.set(key, total);
  return total;
}
/**
 * Forget every tile's denominator. Module-level state outlives the view, so a
 * stale entry would seed the next build on that tile with the OLD total (demolish
 * a half-built farmhouse, place a torch, watch its bar start at ~83%).
 */
export function resetBuildTotals(): void { buildTotals.clear(); }

export function paintPlots(c: FarmCtx) {
  const { plotLayer, world, farm, tex } = c;
  if (!plotLayer || !world || !farm) return;
  for (const s of c.plotSprites) s.destroy();
  c.plotSprites = [];
  // Tiles that no longer carry a building (demolished, or finished) must not keep
  // a denominator — see resetBuildTotals.
  if (buildTotals.size) {
    const live = new Set(farm.buildings.filter((b) => isUnderConstruction(b)).map((b) => `${b.tx},${b.ty}`));
    for (const k of [...buildTotals.keys()]) if (!live.has(k)) buildTotals.delete(k);
  }
  // Placed lights register a flicker entry on the shared anim ticker; drop the
  // entries whose sprite we just destroyed so the list can't grow every repaint.
  if (c.animWater.length) c.animWater = c.animWater.filter((a) => !a.sprite.destroyed);

  // Block 8: plots are wherever the player hoed them — the farm data IS the list
  // of soil beds (every plot row is TILLED or PLANTED; bare grass has no row).
  const activePlots = new Set<string>();
  for (const p of farm.plots) activePlots.add(`${p.tx},${p.ty}`);

  // Owner-picked plot art: each plot cell shows the single-cell FarmLand bed
  // tile in its MIDDLE, and the sand-lipped grass ring is drawn on the 8
  // NEIGHBOUR tiles around it (their flat grass matches the lawn exactly, so
  // only the sand lip + tufts read). Neighbours that are themselves plot
  // cells get no ring — adjacent beds sit side by side, each self-contained.
  const isSoil = (tx: number, ty: number) => activePlots.has(`${tx},${ty}`);
  const RING_DIRS: [number, number, keyof MapTextures['frame']][] = [
    [-1, -1, 'tl'], [0, -1, 't'], [1, -1, 'tr'],
    [-1, 0, 'l'], [1, 0, 'r'],
    [-1, 1, 'bl'], [0, 1, 'b'], [1, 1, 'br'],
  ];
  // Collect ring keys per lawn cell first, then draw CORNERS UNDER EDGES:
  // a corner tile's sand quadrant would otherwise cover the edge tile's
  // tufts and cut a square notch out of the ring (owner-flagged).
  const ringKeys = new Map<string, Set<keyof MapTextures['frame']>>();
  const lawnCells = new Set<string>();          // lawn cells touching soil — get pocket/accent tiles
  for (const cell of activePlots) {
    const [tx, ty] = cell.split(',').map(Number);
    for (const [dx, dy, k] of RING_DIRS) {
      const nx = tx + dx, ny = ty + dy;
      if (isSoil(nx, ny)) continue;
      const key = `${nx},${ny}`;
      lawnCells.add(key);
      let set = ringKeys.get(key);
      if (!set) ringKeys.set(key, (set = new Set()));
      set.add(k);
    }
  }
  const EDGE_ON_TOP = ['tl', 'tr', 'bl', 'br', 't', 'b', 'l', 'r'] as const;
  for (const [cellKey, keys] of ringKeys) {
    const [nx, ny] = cellKey.split(',').map(Number);
    let dz = 0.1;
    for (const k of EDGE_ON_TOP) {
      if (!keys.has(k)) continue;
      const q = decorative(new Sprite(tex!.frame[k]));
      q.scale.set(PX);
      q.x = nx * TILE; q.y = ny * TILE;
      q.zIndex = ny * TILE + dz;
      dz += 0.005;
      plotLayer.addChild(q); c.plotSprites.push(q);
    }
  }

  // Grass pockets between beds (owner-designed in the plot builder): a lawn
  // cell walled in by beds gets the matching "inner grass" tile stacked over
  // its ring — a full hole, a 1-wide channel, a channel dead-end (cap), a
  // channel opening into open lawn (mouth), or concave tuft corners.
  const vchan = (x: number, y: number) => !isSoil(x, y) && isSoil(x - 1, y) && isSoil(x + 1, y);
  const hchan = (x: number, y: number) => !isSoil(x, y) && isSoil(x, y - 1) && isSoil(x, y + 1);
  for (const cell of lawnCells) {
    const [tx, ty] = cell.split(',').map(Number);
    const put = (texture: Texture) => {
      const q = decorative(new Sprite(texture));
      q.scale.set(PX);
      q.x = tx * TILE; q.y = ty * TILE;
      q.zIndex = ty * TILE + 0.15;
      plotLayer!.addChild(q); c.plotSprites.push(q);
    };
    const l = isSoil(tx - 1, ty), r = isSoil(tx + 1, ty), t = isSoil(tx, ty - 1), b = isSoil(tx, ty + 1);
    if (l && r && t && b) put(tex!.inner.hole);
    else if (l && r && b) put(tex!.inner.cap_b);
    else if (l && r && t) put(tex!.inner.cap_t);
    else if (t && b && l) put(tex!.inner.cap_l);
    else if (t && b && r) put(tex!.inner.cap_r);
    else if (l && r) put(tex!.inner.ch_v);
    else if (t && b) put(tex!.inner.ch_h);
    else {
      // Concave tuft corner where two beds meet at a right angle.
      if (t && l) put(tex!.frame.cbr);
      if (t && r) put(tex!.frame.cbl);
      if (b && l) put(tex!.frame.ctr);
      if (b && r) put(tex!.frame.ctl);
      if (!l && !r && !t && !b) {
        const tl = isSoil(tx - 1, ty - 1), tr = isSoil(tx + 1, ty - 1);
        const bl = isSoil(tx - 1, ty + 1), br = isSoil(tx + 1, ty + 1);
        if (vchan(tx, ty + 1) && !bl && !br) put(tex!.inner.mouth_t);
        if (vchan(tx, ty - 1) && !tl && !tr) put(tex!.inner.mouth_b);
        if (hchan(tx + 1, ty) && !tr && !br) put(tex!.inner.mouth_l);
        if (hchan(tx - 1, ty) && !tl && !bl) put(tex!.inner.mouth_r);
      }
    }
  }

  for (const p of farm.plots) {
    const px = p.tx * TILE, py = p.ty * TILE;
    const planted = p.state === 'PLANTED' && !!p.cropId;
    const rem = planted ? liveRemaining(c, p.remainingMs) : 0;
    const ready = planted && (p.ready || rem <= 0);

    // An empty, untended bed ages toward grass: past the halfway mark it browns and
    // fades, so the owner sees which beds are about to disappear (server-driven decay).
    const decay = !planted ? (p.decayStage ?? 0) : 0;
    const decayTint = decay > 0.5 ? 0x9c7c56 : 0xffffff;
    const decayAlpha = decay > 0.5 ? Math.max(0.35, 1 - (decay - 0.5) * 1.1) : 1;

    // A soil cell shows exactly one bed tile (dry or wet — a watered bed is
    // visibly darker). Self-contained per cell.
    const stack = (texture: Texture, dz: number) => {
      const s = decorative(new Sprite(texture));
      s.scale.set(PX);
      s.x = px; s.y = py; s.zIndex = py + dz;
      s.tint = decayTint; s.alpha = decayAlpha;
      plotLayer!.addChild(s); c.plotSprites.push(s);
    };
    stack(tex!.plot.dry, 0.2);
    // Merged-bed overlay (owner-designed): key = which sides also have soil.
    const key = ((isSoil(p.tx - 1, p.ty) ? 'l' : '') + (isSoil(p.tx + 1, p.ty) ? 'r' : '')
      + (isSoil(p.tx, p.ty - 1) ? 't' : '') + (isSoil(p.tx, p.ty + 1) ? 'b' : '')) as OverlayKey;
    if (key) stack(tex!.overlay[key].dry, 0.205);
    if (p.watered) stack(key ? tex!.overlay[key].wet : tex!.plot.wet, 0.207);

    const cell = new Sprite(Texture.WHITE);
    cell.alpha = 0;                                 // invisible full-tile click target
    cell.width = TILE; cell.height = TILE; cell.x = px; cell.y = py; cell.zIndex = py + 0.21;
    cell.eventMode = 'static'; cell.cursor = HOE_CURSOR;   // hoe over any soil plot (till / water / plant / harvest)
    cell.on('pointertap', (e) => {
      e.stopPropagation();                    // a plot tap targets the plot, not the ground under it
      if (c.cam?.dragged || c.buildMode) return;
      const pp = plotAt(c, p.tx, p.ty);
      if (!pp) return;
      const isPlanted = pp.state === 'PLANTED' && !!pp.cropId;
      const isReady = isPlanted && (pp.ready || liveRemaining(c, pp.remainingMs) <= 0);
      // A ready crop auto-equips the hoe and harvests, whatever tool was in hand.
      if (isReady) {
        // Through selectHotbarTool, not a bare assignment: arming a build and then
        // tapping a ready crop used to harvest with the red ghost still stuck to the
        // cursor, because this swap skipped the shared placement teardown.
        if (c.selectedTool !== 'hoe') { selectHotbarTool(c, 'hoe'); paintHotbar(c); }
        if (inToolReach(c, p.tx, p.ty)) { c.pendingAction = null; c.pendingPlot = null; actOnPlot(c, p.tx, p.ty, 'hoe'); }
        else requestPlot(c, p.tx, p.ty, 'hoe');
        return;
      }
      // Still growing and paid-rushable → offer the shortcut right here rather
      // than walking over to be told "still growing…".
      if (isPlanted && canRushCrop(pp)) return openCropRush(c, pp);
      // Wrong tool for a plot. Swallow the tap (an axe must not till), but SAY so
      // — a silent return here reads as "my soil bed is broken", which is exactly
      // what input.ts avoids on the tile path with its "Clear the tree first" hint.
      if (c.selectedTool === 'axe' || c.selectedTool === 'pickaxe')
        return void toast(`Put the ${c.selectedTool} away — a bed is worked with the hoe`);
      // No tool (or hoe): interact with the bed — water then seed.
      requestPlot(c, p.tx, p.ty);
    });
    plotLayer.addChild(cell); c.plotSprites.push(cell);

    if (planted) {
      // §4b: standing crops render in the y-sorted ENTITY layer (not the flat plot
      // layer), so a crop south of a tree draws IN FRONT of it and one to the north
      // draws behind — their feet-y zIndex already matches the tree/hero space. The
      // flat tilled soil above stays in plotLayer, correctly beneath everything.
      const standLayer = c.entityLayer ?? plotLayer;
      const ct = c.cropTex.get(p.cropId!)?.[cropStage(c, p.cropId!, rem, p.watered, ready)];
      if (ct) {
        const crop = decorative(new Sprite(ct));
        crop.anchor.set(0.5, 1); crop.scale.set(TILE / 16);
        crop.x = px + TILE / 2; crop.y = py + TILE + 6; crop.zIndex = py + TILE;
        standLayer.addChild(crop); c.plotSprites.push(crop);
      }
      if (ready) {
        const bar = decorative(new Sprite(Texture.WHITE));
        bar.width = TILE; bar.height = 4; bar.tint = 0x7bc86b;
        bar.x = px; bar.y = py + TILE - 4; bar.zIndex = py + TILE + 1;
        standLayer.addChild(bar); c.plotSprites.push(bar);
      } else {
        // Growth progress bar — fills as the crop ripens so the player can see at a
        // glance how close a bed is to harvest (no hover needed). Repainted every
        // second by the farm ticker, so it counts up live. Amber → green near ready.
        // A DRY bed is pinned at 0 and drawn cold: the server does not tick an
        // unwatered seed at all, so the amber "nearly there" bar it used to draw
        // (from a remainingMs that never moves) read as progress and is the single
        // most common reason a player thinks the game is stuck.
        const total = cropById(c, p.cropId!)?.growthMs ?? 1;
        const frac = growthFraction(rem, total, p.watered);
        const track = decorative(new Sprite(Texture.WHITE));
        track.width = TILE; track.height = 4; track.tint = 0x1c150c; track.alpha = 0.7;
        track.x = px; track.y = py + TILE - 4; track.zIndex = py + TILE + 1;
        standLayer.addChild(track); c.plotSprites.push(track);
        const fill = decorative(new Sprite(Texture.WHITE));
        fill.width = Math.max(1, TILE * frac); fill.height = 4;
        fill.tint = !p.watered ? 0x4a7fb5 : frac > 0.66 ? 0xa6d95a : frac > 0.33 ? 0xe6c14a : 0xd98a3a;
        fill.x = px; fill.y = py + TILE - 4; fill.zIndex = py + TILE + 2;
        standLayer.addChild(fill); c.plotSprites.push(fill);
        // Watered beds ripen on a clock, so they get the same live countdown a
        // building does. A dry bed is NOT growing, and a blank tile said nothing
        // at all about why — it now says so in words, in the same slot. Plain text
        // (no 💧): this is a Pixi Text, so an emoji would draw as a system glyph
        // over the pixel art.
        if (p.watered) {
          const t = timerLabel(fmtCountdown(rem), px + TILE / 2, py - 2, py + TILE + 3);
          standLayer.addChild(t); c.plotSprites.push(t);
        } else {
          const t = timerLabel('NEEDS WATER', px + TILE / 2, py - 2, py + TILE + 3);
          t.style.fill = 0x9fd6ff;
          standLayer.addChild(t); c.plotSprites.push(t);
        }
      }
    }
  }

  // Buildings (Block 8): torches / fences the player placed. Rendered anchored at
  // the base so they y-sort with the hero. Tap in build mode is handled by the
  // stage; a plain tap on a building removes it (with the build panel open).
  // Fences AUTO-CONNECT: the sprite is chosen from which sides another fence of
  // the same material continues through, so a run drawn by tapping tiles resolves
  // its own corners, tees and end caps. Build the tile lookup once per repaint
  // rather than scanning the building list four times per fence.
  const buildingAt = new Map<string, string>();
  for (const b of farm.buildings) buildingAt.set(`${b.tx},${b.ty}`, b.kind);
  const kindAt = (tx: number, ty: number) => buildingAt.get(`${tx},${ty}`) ?? null;

  for (const b of farm.buildings) {
    const s = new Sprite(Texture.EMPTY);
    const litProp = LIGHT_BUILDINGS[b.kind];
    // The sprite a fence actually draws (fence_cnw, fence_stone_x, …). Everything
    // else draws the sprite named by its kind.
    // Cobble roads pave an AREA, so they resolve their own verge from the eight
    // surrounding tiles (map/road-connect.ts) the way a fence resolves its corners.
    // A furnace has three reads, one per state of its load: cold (nothing in it),
    // BURNING (the animated strip, handled below), and lit-but-finished — embers
    // still glowing over bars nobody has collected, which is the nudge to walk
    // over and open it.
    const spriteKind = isFenceKind(b.kind) ? fenceSprite(b.kind, b.tx, b.ty, kindAt)
      : isRoadKind(b.kind) ? roadSprite(b.kind, b.tx, b.ty, kindAt)
      : furnaceSprite(b.kind, farm.smelting) ?? b.kind;
    // Timed construction: while the server says it isn't ready, the tile shows the
    // building it is BECOMING, ghosted, with a timber frame over it. The countdown is
    // interpolated from the snapshot; when it runs out we ASK the server rather than
    // promoting the building ourselves (see construction.ts) — the next paint gets
    // the finished art.
    //
    // It used to draw one of two generic scaffold sheets at a hardcoded 2.2 / 1.1
    // tiles, which had no relation to the kind: every structure wore the same
    // HOUSE-shaped carcass, three tiles too small for a farmhouse and half a tile
    // too tall for a mine shaft. Ghosting the real sprite is inherently the right
    // size and unmistakably the right building, and the pack has no per-kind
    // construction art to slice (nor may any be generated).
    const building = isUnderConstruction(b);
    const remain = building ? liveRemaining(c, b.remainingMs ?? 0) : 0;
    if (building && remain <= 0) requestFinishRefresh(c);
    if (building) {
      loadBuildingTex(spriteKind, (t) => {
        if (s.destroyed || !t.height) return;
        s.texture = t;
        const sc = buildingScale(b.kind);
        s.scale.set(sc);
        // Cold, washed-out and semi-transparent: the "not here yet" read. Without it
        // a paused build is indistinguishable from a working one at a glance.
        s.tint = 0x7f92aa; s.alpha = 0.55;
        // The frame sits ON the ghost and is fitted INSIDE its drawn box (min of the
        // two ratios, never larger), so a barn gets a barn-sized frame and a
        // workbench a workbench-sized one. Tall art takes the tall sheet.
        const w = t.width * sc, h = t.height * sc;
        loadBuildingTex(h >= w ? 'scaffold' : 'scaffold_small', (f) => {
          if (s.destroyed || !f.width || !f.height) return;
          const frame = decorative(new Sprite(f));
          frame.anchor.set(0.5, 1);
          frame.scale.set(Math.min(w / f.width, h / f.height));
          // Positioned from the tile, NOT from s: a cached texture runs this callback
          // synchronously, before s.x/s.y are assigned further down.
          frame.x = b.tx * TILE + TILE / 2; frame.y = b.ty * TILE + TILE;
          frame.zIndex = buildingZ(b.tx, b.ty) + 0.5;
          (c.entityLayer ?? plotLayer).addChild(frame);
          c.plotSprites.push(frame);
        });
      });
    } else if (b.kind === 'furnace' && farm.smelting?.burning) {
      // A furnace with a load in it BURNS. The strip is sliced in map/lights.ts and
      // played by the shared anim ticker below — the same one the torches use — so
      // a working furnace is visible from across the farm without a second loop or
      // a per-frame request. When the load finishes, the next farm snapshot drops
      // `burning` and this repaints as the cold sprite.
      void furnaceFrames().then((frames) => {
        if (s.destroyed || !frames.length) return;
        s.texture = frames[0];
        c.animWater.push({ sprite: s, frames, fps: FURNACE_BURN.fps, t: 0, i: 0 });
      });
    } else if (litProp) {
      // Torches and lanterns BURN — they run the pack's flicker loop through the
      // shared anim ticker (the same one the campfire and the coastline use), so a
      // placed light is animated rather than one frozen frame.
      void lightFrames(litProp).then((frames) => {
        if (s.destroyed || !frames.length) return;
        s.texture = frames[0];
        c.animWater.push({ sprite: s, frames, fps: LIGHT_FPS[litProp], t: 0, i: 0 });
      });
    } else {
      loadBuildingTex(spriteKind, (t) => { if (!s.destroyed) s.texture = t; });
    }
    s.anchor.set(0.5, 1);
    s.scale.set(buildingScale(b.kind));
    s.x = b.tx * TILE + TILE / 2; s.y = b.ty * TILE + TILE; s.zIndex = buildingZ(b.tx, b.ty);
    // Roads/paths/bridges are GROUND: they belong under the hero, under props and
    // under the plot beds, not y-sorted against them. Negative zIndex inside the
    // plot layer (which sits below the entity layer) puts them at the bottom of
    // the stack while still ordering road-over-road by row.
    const ground = isGroundKind(b.kind);
    if (ground) s.zIndex = -1_000_000 + b.ty;
    s.eventMode = 'static'; s.cursor = 'pointer';
    // The route is recomputed at event time (not captured here): build mode can be
    // armed, and the build can finish, between this paint and the tap.
    const route = () => buildingPointerRoute({
      kind: b.kind,
      underConstruction: b.isReady === false,
      buildMode: !!c.buildMode,
      dragMagnitude: c.cam?.dragMagnitude ?? 0,
    });
    s.on('pointertap', (e) => {
      const r = route();
      // 'walk'/'tool' fall through untouched — see buildingPointerRoute.
      if (r === 'walk' || r === 'tool') return;
      e.stopPropagation();
      press = null;          // handled here; the rescue below must not fire too
      runBuildingRoute(c, b, r, (e.global.x), (e.global.y));
    });
    // …and the press, so a tap survives the sprite being rebuilt mid-gesture.
    // farm.ts repaints the plots every second, and paintPlots destroys and
    // recreates EVERY sprite; a repaint landing between pointerdown and pointerup
    // leaves Pixi resolving the tap to the nearest still-mounted ancestor (the
    // entity layer), which bubbles to the stage and walks the hero instead. It is
    // stochastic (~press-duration/1s, worse on touch) and is the second half of
    // "sometimes the building just doesn't respond".
    s.on('pointerdown', (e) => {
      const r = route();
      if (r === 'walk' || r === 'tool') return;
      e.stopPropagation();   // the camera reads pointerdown in the CAPTURE phase, so panning still works
      press = { c, b, sprite: s };
      armPressRescue();
    });
    // §4b: buildings stand up too → y-sort them with trees/hero in the entity layer.
    // Ground decoration stays in the plot layer, which draws below it.
    (ground ? plotLayer : (c.entityLayer ?? plotLayer)).addChild(s); c.plotSprites.push(s);

    if (building) {
      const layer = c.entityLayer ?? plotLayer;
      const bx = b.tx * TILE, by = b.ty * TILE;
      // Plain text only: this is a Pixi Text, not DOM, so richText cannot swap an
      // emoji for its pack sprite — a 🔨 here would render as a system glyph over
      // the pixel art. The countdown alone reads as "under construction".
      const label = timerLabel(fmtCountdown(remain), bx + TILE / 2, by - 2, by + TILE + 3);
      layer.addChild(label); c.plotSprites.push(label);
      // BUILDINGS.buildMin mirrors the server's buildMs; the bar's denominator is
      // tracked per tile (see buildBarTotal).
      const full = (BUILDINGS[b.kind]?.buildMin ?? 0) * 60_000;
      const total = buildBarTotal(b.tx, b.ty, full, remain);
      const frac = total > 0 ? Math.min(1, Math.max(0, 1 - remain / total)) : 1;
      const track = decorative(new Sprite(Texture.WHITE));
      track.width = TILE; track.height = 4; track.tint = 0x1c150c; track.alpha = 0.7;
      track.x = bx; track.y = by + TILE - 4; track.zIndex = by + TILE + 1;
      layer.addChild(track); c.plotSprites.push(track);
      const fill = decorative(new Sprite(Texture.WHITE));
      fill.width = Math.max(1, TILE * frac); fill.height = 4; fill.tint = 0xd9a13a;
      fill.x = bx; fill.y = by + TILE - 4; fill.zIndex = by + TILE + 2;
      layer.addChild(fill); c.plotSprites.push(fill);
    } else {
      buildTotals.delete(`${b.tx},${b.ty}`);
    }
  }

  // Debug grid: outline every bed; a cell only ever affects itself.
  if (c.gridOn) {
    const g = decorative(new Graphics());
    g.zIndex = 1e6;
    for (const p of farm.plots) {
      g.rect(p.tx * TILE, p.ty * TILE, TILE, TILE).fill({ color: 0xffd75e, alpha: 0.22 });
      g.rect(p.tx * TILE, p.ty * TILE, TILE, TILE).stroke({ color: 0xffffff, alpha: 0.35, width: 1 });
    }
    plotLayer.addChild(g); c.plotSprites.push(g);
  }

  // Hide ambient scatter (grass/flowers/mushrooms) sitting under the tilled plots so
  // it doesn't poke through the soil.
  clearDecorUnderPlots(c);
}

/**
 * Grass an empty bed back over (Alt-tap with the hoe, or the seed panel's button).
 *
 * NO CONFIRMATION. OWNER (2026-08-17): "no need to create confirmation of the
 * dialog for grass over the bed." It used to open a confirm() on every clear; that
 * is one modal per tile while re-laying a farm, and an EMPTY bed is worth one hoe
 * swing — the undo is tilling it again.
 *
 * The GUARD stays, and it is a different rule: a bed with a crop growing in it is
 * refused outright (here, in tillBlocker, and again on the server). That protects a
 * paid-for seed and the hours since it went in — no dialog is involved, the action
 * simply does not happen.
 */
export function clearBed(c: FarmCtx, tx: number, ty: number) {
  const p = plotAt(c, tx, ty);
  if (!p) return void toast('Nothing to grass over — this is not a soil bed');
  if (p.state === 'PLANTED' && p.cropId) return void toast('Harvest the crop before you grass this bed over 🌾');
  if (!affordEnergy(c, 'till')) return;
  void guard('field.clearPlot', async (actionId) => {
    playSfx('hoe_till');
    await c.player?.playAction('till', dirToTile(c, tx, ty));
    setFarm(c, await fieldApi.clearPlot(tx, ty, actionId));
    toast('Grassed over 🌿 — the bed is gone');
    await c.game.refreshHud();
    refreshBag(c);
    // The soil sprite is drawn from the plot list, and the ambient scatter under it
    // was hidden when the bed appeared — repaint so the tile stops looking tilled.
    paintPlots(c);
  }, `${tx},${ty}`);
}

// ─── Walk-to-plot-tile, then act ──────────────────────────────────────────────
// Clicking a plot walks the hero to it first (same feel as trees/rocks); the
// action fires on arrival with state re-read from the live farm data, so a slow
// walk can't act on a stale snapshot.
export function actOnPlot(c: FarmCtx, tx: number, ty: number, tool?: 'hoe', intent: 'auto' | 'clear' = 'auto') {
  if (intent === 'clear') return clearBed(c, tx, ty);
  if (tool === 'hoe') {
    // Hoe does exactly two things: till bare cleared ground, harvest a ready crop.
    // Pass the intent EXPLICITLY. It used to be omitted, so hoeAction fell back to
    // the live Alt flag: a walk-to-plot queued with intent 'auto' resolved against
    // whatever the modifier was on arrival, and holding Alt while the hero walked
    // made an empty bed return 'clear' — which matches neither branch below and fell
    // through to watering the bed instead.
    const act = hoeAction(c, tx, ty, intent);
    // Cost checked BEFORE the hoe swing: the player used to till, watch the
    // animation, and only then be told the bar was empty.
    if (act === 'till') return void (affordEnergy(c, 'till') && guard('field.till', async (actionId) => {
      playSfx('hoe_till');                 // with the swing, not after the server answers
      await c.player?.playAction('till', dirToTile(c, tx, ty));
      setFarm(c, await fieldApi.till(tx, ty, actionId));
      toast('Tilled 🌱 — now water the bed');
      await c.game.refreshHud();
      refreshBag(c);
      paintPlots(c);
    }));
    if (act === 'harvest') return void (affordEnergy(c, 'harvest') && guard('field.harvest', async (actionId) => {
      playSfx('harvest_crop');
      await c.player?.playAction('till', dirToTile(c, tx, ty));
      const r = await fieldApi.harvest(tx, ty, actionId);
      if (r.leveledUp) playSfx('level_up');
      c.onDrops?.(r.drops);
      const name = cropById(c, r.cropId)?.name ?? r.cropId;
      toast(`${r.quantity} ${name} on the ground${r.regrows ? ' · water it to fruit again' : ''}${r.leveledUp ? ' · LEVEL UP!' : ''}`);
      await c.load(); await c.game.refreshHud();
      refreshBag(c);
    }));
    // The hoe can't till/harvest here — but if it's an existing bed that still
    // needs watering or seeding, do THAT instead of silently doing nothing.
    // (Before this, watering "didn't work" whenever the hoe was still selected
    // after tilling — you had to deselect it first, which nobody guesses.)
    if (!plotAt(c, tx, ty)) return;             // truly bare ground → nothing to do
  }
  const p = plotAt(c, tx, ty);
  if (!p) return;
  const planted = p.state === 'PLANTED' && !!p.cropId;
  const ready = planted && (p.ready || liveRemaining(c, p.remainingMs) <= 0);
  onPlot(c, tx, ty, ready, planted, p.watered);
}

export function onPlot(c: FarmCtx, tx: number, ty: number, ready: boolean, planted: boolean, watered: boolean) {
  // Full cycle: hoe cleared ground → water the bed → seed it → wait → harvest.
  if (planted) {
    if (ready) return void (affordEnergy(c, 'harvest') && guard('field.harvest', async (actionId) => {   // harvest with the hoe
      playSfx('harvest_crop');
      await c.player?.playAction('till', dirToTile(c, tx, ty));
      const r = await fieldApi.harvest(tx, ty, actionId);
      if (r.leveledUp) playSfx('level_up');
      c.onDrops?.(r.drops);
      const name = cropById(c, r.cropId)?.name ?? r.cropId;
      toast(`${r.quantity} ${name} on the ground${r.regrows ? ' · water it to fruit again' : ''}${r.leveledUp ? ' · LEVEL UP!' : ''}`);
      await c.load(); await c.game.refreshHud();
      refreshBag(c);
    }));
    if (!watered) return void (affordEnergy(c, 'water') && guard('field.water', async (actionId) => {  // regrow cycle needs water
      playSfx('water_pour');
      await c.player?.playAction('water', dirToTile(c, tx, ty));
      setFarm(c, await fieldApi.water(tx, ty, actionId));
      toast('Watered 💧 — growing now');
      await c.game.refreshHud();
      refreshBag(c);
      paintPlots(c);
    }));
    // Reached with the hoe in hand (toolTap route). Same offer as a bare tap.
    const growing = plotAt(c, tx, ty);
    if (growing && canRushCrop(growing)) return openCropRush(c, growing);
    return toast('Still growing…');
  }
  if (!watered) return void (affordEnergy(c, 'water') && guard('field.water', async (actionId) => {    // water the dry bed before seeding
    playSfx('water_pour');
    await c.player?.playAction('water', dirToTile(c, tx, ty));
    setFarm(c, await fieldApi.water(tx, ty, actionId));
    toast('Watered 💧 — ready to seed');
    await c.game.refreshHud();
      refreshBag(c);
    paintPlots(c);
  }));
  openPlanter(c, tx, ty);                                  // wet bed → seed
}

// ─── Crop picker (DOM modal) ─────────────────────────────────────────────────
// Cached so the picker can render instantly on repeat opens; refreshed each open.
let lastKnownLevel = 1;

export async function openPlanter(c: FarmCtx, tx: number, ty: number) {
  closePlanter(c);
  playSfx('ui_open');
  // Player level gates which seeds are choosable (and greys the rest).
  try { lastKnownLevel = (await shopApi.wallet()).level ?? lastKnownLevel; } catch { /* keep last */ }
  const level = lastKnownLevel;
  // What seed this player is actually holding. The picker used to show the crop
  // CATALOGUE only, so the seeds he pressed at the market were invisible here and
  // there was no way to tell a free planting from a paid one (owner bug: "I have
  // 16 wheat, 28 eggplant and 6 corn seeds but see only wheat"). Read fresh on
  // every open — a planting spends one, and a stale count would advertise a free
  // seed the server no longer has. Failure is non-fatal: no counts, catalogue only.
  let seedsOwned: Record<string, number> = {};
  try {
    const board = await fieldApi.seeds();
    seedsOwned = Object.fromEntries(board.rates.filter((r) => r.seedsOwned > 0).map((r) => [r.cropId, r.seedsOwned]));
  } catch { /* counts are an enhancement, never a blocker on planting */ }
  // Until the tutorial is finished, offer ONLY the first starter seed — the full
  // catalogue appears once the player graduates.
  const tutorialActive = tutorialStage() !== 'done';

  const card = el('div', 'bg-surface border border-border rounded-lg p-4 w-[340px] max-w-[90vw] shadow-lift sheet-card', {},
    el('div', 'flex justify-between items-center mb-3', {},
      el('h3', 'font-heading text-gold', {}, 'Plant a seed'),
      el('button', 'tb-close-x text-text-dim hover:text-text text-lg', { onClick: () => closePlanter(c) }, '×')));
  card.append(waterNotice());
  card.append(buildSeedList(c.crops, level, tutorialActive, (crop) => {
    // Told at the moment the seed is chosen, not after the seed is spent.
    if (!affordEnergy(c, 'plant')) return;
    void guard('field.plant', async (actionId) => {
      closePlanter(c); playSfx('plant_seed');
      setFarm(c, await fieldApi.plant(tx, ty, crop.id, actionId));
      toast(`Planted ${crop.name} — water it or it won't grow`);
      await c.game.refreshHud();
      refreshBag(c); paintPlots(c);
    }, `${tx},${ty}`);
  }, seedsOwned));

  // The touch way out of a bed. Alt-tap is a keyboard gesture and phones have no
  // Alt, so without this the owner's "grass it up again" would be desktop-only —
  // and this panel is the one place you are already looking at an empty bed.
  card.append(el('button',
    'w-full mt-3 py-2 rounded border border-border text-text-dim hover:text-text text-sm',
    { onClick: () => { closePlanter(c); clearBed(c, tx, ty); } },
    '🌿 Grass this bed over'));

  // z-[70] sits ABOVE the tutorial coach card (z-[60]) so the popup is never
  // covered while the player is choosing a seed.
  // openOverlay (not a bare append): it attaches Escape, so every panel has a way
  // out even when the page chrome its close button sits under is hidden.
  const wrap = modalWrap(() => closePlanter(c), card, 'z-[70]');
  openOverlay(c, wrap);
}
